import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/components/visitone/visitone-provider'
import { DESTINATIONS, type DestinationId } from '@/components/visitone/navigation-model'
import { SETUP_ORDER_TYPES_PATH } from '@/lib/routing'

/*
 * PrimaryNavigation: the six-item rail. The active item is marked by an accent
 * bar and a weight change (never colour alone). Favourites sit below a divider.
 * The rail lists `railDestinations` (permitted destinations, plus Setup when
 * Landlords is held without that destination). Favourites stay inside
 * permitted `destinations`, so that Setup entry does not bring Order types.
 * `current` is null on a refused or unknown route, so no item is marked.
 */
export function PrimaryNav({
  current,
  destinations,
  railDestinations = destinations,
  collapsed = false,
  onNavigate,
  onOpenRoute,
  hasFeature = () => true,
}: {
  current: DestinationId | null
  /** Permitted destinations. Favourites are shown only for these. */
  destinations: DestinationId[]
  /** Rail entries, in any order; rendered in rail order. Defaults to `destinations`. */
  railDestinations?: DestinationId[]
  collapsed?: boolean
  onNavigate?: (id: DestinationId) => void
  onOpenRoute?: (route: string) => void
  /** When a favourite names a plan-limited feature, it is shown only when held. */
  hasFeature?: (featureId: string) => boolean
}) {
  const t = useT()
  const favourites = [
    { label: t('core.nav.favouriteTonight'), destination: 'programme' as const, route: undefined, feature: undefined },
    { label: t('core.nav.orderTypes'), destination: 'setup' as const, route: SETUP_ORDER_TYPES_PATH, feature: undefined },
  ].filter(
    (favourite) =>
      destinations.includes(favourite.destination) &&
      (favourite.feature === undefined || hasFeature(favourite.feature)),
  )

  return (
    <nav
      aria-label={t('core.nav.primaryNavigation')}
      className="flex h-full flex-col gap-4 border-r border-line bg-surface p-3"
    >
      <ul className="flex flex-col gap-1">
        {DESTINATIONS.filter((destination) => railDestinations.includes(destination.id)).map((destination) => {
          const Icon = destination.icon
          const active = destination.id === current
          const label = t(destination.labelKey)
          return (
            <li key={destination.id}>
              <button
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => onNavigate?.(destination.id)}
                title={collapsed ? label : undefined}
                className={cn(
                  'relative flex min-h-11 w-full items-center gap-3 rounded-sm px-3 py-2 text-left text-sm transition-colors',
                  collapsed && 'justify-center px-0',
                  active
                    ? 'bg-selected text-fg-strong'
                    : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                )}
                style={active ? { fontWeight: 650 } : undefined}
              >
                {active ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-lg bg-accent"
                  />
                ) : null}
                <Icon aria-hidden="true" className="size-5 shrink-0" />
                {!collapsed ? <span className="min-w-0 wrap-anywhere">{label}</span> : null}
              </button>
            </li>
          )
        })}
      </ul>

      {!collapsed && favourites.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-line pt-4">
          <p className="px-3 text-xs tracking-wide text-fg-muted">
            {t('core.nav.favourites').toUpperCase()}
          </p>
          {favourites.map((favourite) => (
            <button
              key={favourite.label}
              type="button"
              onClick={favourite.route ? () => onOpenRoute?.(favourite.route as string) : undefined}
              className="flex min-h-11 w-full items-center gap-3 rounded-sm px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
            >
              <Star aria-hidden="true" className="size-5 shrink-0 text-accent" />
              <span className="min-w-0 wrap-anywhere">{favourite.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </nav>
  )
}
